import { useMemo, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Search, X, Package, Trash2, Pencil, PlusCircle } from "lucide-react";
import { formatDate } from "@/lib/dates";
import { HistoryDetailDialog } from "@/components/HistoryDetailDialog";

const SUB_TABS = [
  { v: "draft", l: "Draft" },
  { v: "pending", l: "Pending" },
  { v: "received", l: "Accepted" },
  { v: "rejected", l: "Rejected" },
];

function statusBadge(status) {
  const map = {
    Pending: "bg-[#FDF3D8] text-[#8A6C1A] border-[#E8CE84]",
    Received: "bg-[#D8EBDE] text-[#0B5C33] border-[#7FBB94]",
    Rejected: "bg-[#F8DADA] text-[#8B2020] border-[#E39B9B]",
    Draft: "bg-[#EFE6D3] text-[#7A5A24] border-[#D4C39B]",
  };
  return (
    <Badge
      variant="outline"
      className={`text-[11px] font-medium ${map[status] || ""}`}
    >
      {status}
    </Badge>
  );
}

function materialSummary(row) {
  if (row.materials && row.materials.length > 0) {
    return row.materials
      .map((m) => `${m.material_type} (${Number(m.weight_kg || 0).toFixed(1)} kg)`)
      .join(", ");
  }
  if (row.material_type) {
    const kg = row.received_weight_kg ?? row.estimated_weight_kg ?? 0;
    return `${row.material_type} (${Number(kg).toFixed(1)} kg)`;
  }
  return "—";
}

function draftMaterialSummary(draft) {
  const payload = draft.payload || {};
  const rows = payload.materialRows || payload.materials || [];
  const filtered = rows.filter((r) => r.material && r.weight);
  if (filtered.length > 0) {
    return filtered
      .map((r) => `${r.material || r.material_type} (${Number(r.weight || r.weight_kg || 0).toFixed(1)} kg)`)
      .join(", ");
  }
  if (payload.material) {
    return `${payload.material} (${Number(payload.weight || 0).toFixed(1)} kg)`;
  }
  return "—";
}

export default function MyRecords({
  records,
  drafts,
  subTab,
  setSubTab,
  filters,
  setFilters,
  agents,
  onResume,
  onDelete,
  onStartNew,
  onResubmit,
}) {
  const { filterFrom, filterTo, filterAgent, filterRef } = filters;
  const { setFilterFrom, setFilterTo, setFilterAgent, setFilterRef } = setFilters;
  const [detail, setDetail] = useState(null);

  const filtered = useMemo(() => {
    const from = filterFrom ? new Date(filterFrom + "T00:00:00") : null;
    const to = filterTo ? new Date(filterTo + "T23:59:59") : null;
    return (records || []).filter((r) => {
      // Status filter (sub-tab)
      if (subTab === "pending" && r.status !== "Pending") return false;
      if (subTab === "received" && r.status !== "Received") return false;
      if (subTab === "rejected" && r.status !== "Rejected") return false;
      // Date filter (pickup_at)
      const d = r.pickup_at ? new Date(r.pickup_at) : null;
      if (from && (!d || d < from)) return false;
      if (to && (!d || d > to)) return false;
      // Agent filter
      if (filterAgent && filterAgent !== "__all__" && r.agent_id !== filterAgent) return false;
      // Reference number filter (substring, case-insensitive)
      if (filterRef && filterRef.trim()) {
        const q = filterRef.trim().toLowerCase();
        if (!(r.reference_number || "").toLowerCase().includes(q)) return false;
      }
      return true;
    });
  }, [records, subTab, filterFrom, filterTo, filterAgent, filterRef]);

  const draftsFiltered = useMemo(() => {
    if (subTab !== "draft") return drafts || [];
    return drafts || [];
  }, [drafts, subTab]);

  const counts = useMemo(() => {
    const c = { draft: (drafts || []).length, pending: 0, received: 0, rejected: 0 };
    for (const r of records || []) {
      if (r.status === "Pending") c.pending += 1;
      else if (r.status === "Received") c.received += 1;
      else if (r.status === "Rejected") c.rejected += 1;
    }
    return c;
  }, [records, drafts]);

  const hasFilters = filterFrom || filterTo || (filterAgent && filterAgent !== "__all__") || filterRef;

  return (
    <div className="space-y-4" data-testid="my-records-panel">
      {/* Sub-tabs */}
      <div className="flex flex-wrap items-center gap-1 border-b border-[#D2E4D6]" data-testid="records-subtabs">
        {SUB_TABS.map((t) => (
          <button
            key={t.v}
            type="button"
            onClick={() => setSubTab(t.v)}
            data-testid={`records-subtab-${t.v}`}
            className={`px-3 py-2 text-sm font-medium border-b-2 transition-colors ${
              subTab === t.v
                ? "border-[#056839] text-[#056839]"
                : "border-transparent text-[#3F3F3F]/70 hover:text-[#056839]"
            }`}
          >
            {t.l} ({counts[t.v] || 0})
          </button>
        ))}
      </div>

      {/* Filters (hidden on draft sub-tab) */}
      {subTab !== "draft" && (
        <Card className="border-[#D2E4D6] rounded-xl" data-testid="records-filters">
          <CardContent className="p-4 grid grid-cols-1 md:grid-cols-4 gap-3">
            <div>
              <Label className="text-xs text-[#3F3F3F]/70">From</Label>
              <Input
                type="date"
                value={filterFrom}
                onChange={(e) => setFilterFrom(e.target.value)}
                data-testid="records-filter-from"
              />
            </div>
            <div>
              <Label className="text-xs text-[#3F3F3F]/70">To</Label>
              <Input
                type="date"
                value={filterTo}
                onChange={(e) => setFilterTo(e.target.value)}
                data-testid="records-filter-to"
              />
            </div>
            <div>
              <Label className="text-xs text-[#3F3F3F]/70">Agent / Source</Label>
              <Select value={filterAgent} onValueChange={setFilterAgent}>
                <SelectTrigger data-testid="records-filter-agent">
                  <SelectValue placeholder="All" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">All agents</SelectItem>
                  {(agents || []).map((a) => (
                    <SelectItem key={a.id} value={a.id}>
                      {a.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-[#3F3F3F]/70">Reference #</Label>
              <div className="relative">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-[#3F3F3F]/40" />
                <Input
                  value={filterRef}
                  onChange={(e) => setFilterRef(e.target.value)}
                  placeholder="Search reference"
                  className="pl-8"
                  data-testid="records-filter-ref"
                />
              </div>
            </div>
            {hasFilters && (
              <div className="md:col-span-4">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setFilterFrom("");
                    setFilterTo("");
                    setFilterAgent("__all__");
                    setFilterRef("");
                  }}
                  data-testid="records-clear-filters"
                >
                  <X className="h-3 w-3 mr-1" /> Clear filters
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Draft sub-tab */}
      {subTab === "draft" && (
        <Card className="border-[#E4D5B7] bg-[#FBF6EB] rounded-xl">
          <CardContent className="p-0">
            {draftsFiltered.length === 0 ? (
              <div className="p-6 text-center text-sm text-[#3F3F3F]/60" data-testid="records-empty-draft">
                No drafts saved.{" "}
                <button
                  type="button"
                  className="text-[#056839] underline"
                  onClick={onStartNew}
                  data-testid="records-start-new"
                >
                  Start a new collection
                </button>
              </div>
            ) : (
              <ul className="divide-y divide-[#E4D5B7]">
                {draftsFiltered.map((d) => {
                  const p = d.payload || {};
                  return (
                    <li
                      key={d.id}
                      className="p-3 flex items-center gap-3"
                      data-testid={`records-draft-row-${d.id}`}
                    >
                      <div className="w-9 h-9 rounded-md bg-[#EFE6D3] flex items-center justify-center">
                        <Package className="h-4 w-4 text-[#A0522D]" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-[#3F3F3F] truncate">
                          {p.siteName || p.source_location || "Untitled draft"}
                        </div>
                        <div className="text-xs text-[#3F3F3F]/60 truncate">
                          {draftMaterialSummary(d)} · updated {formatDate(d.updated_at || d.created_at)}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={() => onResume(d)}
                          data-testid={`records-draft-resume-${d.id}`}
                        >
                          <Pencil className="h-3 w-3 mr-1" /> Resume
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          className="text-red-600 hover:text-red-700"
                          onClick={() => onDelete(d.id)}
                          data-testid={`records-draft-delete-${d.id}`}
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
            <div className="p-3 border-t border-[#E4D5B7] flex justify-end">
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="border-[#056839] text-[#056839] hover:bg-[#056839] hover:text-white"
                onClick={onStartNew}
                data-testid="records-new-from-draft-tab"
              >
                <PlusCircle className="h-4 w-4 mr-1" /> New collection
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Submitted records list */}
      {subTab !== "draft" && (
        <Card className="border-[#D2E4D6] rounded-xl">
          <CardContent className="p-0">
            {filtered.length === 0 ? (
              <div className="p-6 text-center text-sm text-[#3F3F3F]/60" data-testid="records-empty">
                No records match this view.
              </div>
            ) : (
              <ul className="divide-y divide-[#D2E4D6]">
                {filtered.map((r) => (
                  <li
                    key={r.id}
                    className="p-0"
                    data-testid={`records-row-${r.id}`}
                  >
                    <div className="flex items-stretch">
                      <button
                        type="button"
                        onClick={() => setDetail({ kind: "received", data: r })}
                        className="flex-1 text-left p-3 flex items-center gap-3 hover:bg-[#EAF3EC]/50 transition-colors min-w-0"
                        data-testid={`records-row-btn-${r.id}`}
                      >
                        <div className="w-9 h-9 rounded-md bg-[#EAF3EC] flex items-center justify-center shrink-0">
                          <Package className="h-4 w-4 text-[#056839]" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <div className="text-sm font-medium text-[#3F3F3F] truncate">
                              {r.source_location || "—"}
                            </div>
                            {statusBadge(r.status)}
                            {r.reference_number && (
                              <span className="text-[11px] text-[#3F3F3F]/50">
                                Ref: {r.reference_number}
                              </span>
                            )}
                          </div>
                          <div className="text-xs text-[#3F3F3F]/60 truncate">
                            {materialSummary(r)} · {formatDate(r.pickup_at)}
                            {r.destination_facility_name ? ` · ${r.destination_facility_name}` : ""}
                          </div>
                          {r.status === "Rejected" && r.rejection_reason && (
                            <div className="text-xs text-red-700 truncate">
                              Reason: {r.rejection_reason}
                            </div>
                          )}
                        </div>
                      </button>
                      {r.status === "Rejected" && onResubmit && (
                        <div className="flex items-center pr-3">
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            className="border-[#A0522D] text-[#A0522D] hover:bg-[#A0522D] hover:text-white"
                            onClick={(e) => { e.stopPropagation(); onResubmit(r); }}
                            data-testid={`records-resubmit-${r.id}`}
                          >
                            <Pencil className="h-3 w-3 mr-1" /> Edit &amp; Resubmit
                          </Button>
                        </div>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      )}

      <HistoryDetailDialog detail={detail} onClose={() => setDetail(null)} />
    </div>
  );
}
